import { Accordion, FormStack, Input } from "@marekl/adminer/components";
import type { ThemeState } from "./blocksModel";
import styles from "./TemplateDetailPage.module.css";

const COLOR_FIELDS: { key: keyof ThemeState; label: string }[] = [
  { key: "background_color", label: "Page background color" },
  { key: "content_background", label: "Content background color" },
  { key: "text_color", label: "Text color" },
  { key: "link_color", label: "Link color" },
  { key: "button_color", label: "Button color" },
  { key: "button_text_color", label: "Button text color" },
];

/** Simple #rrggbb check so the native color input only gets valid values. */
function isHexColor(v: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(v);
}

interface ThemePanelProps {
  theme: ThemeState;
  onChange: (theme: ThemeState) => void;
}

/**
 * Collapsible theme editor: width, colors (native picker + hex), font,
 * language. An `Accordion.Item` unmounts its body while closed — safe here,
 * because all state lives above in `theme`.
 */
export function ThemePanel({ theme, onChange }: ThemePanelProps) {
  function set<K extends keyof ThemeState>(key: K, value: ThemeState[K]) {
    onChange({ ...theme, [key]: value });
  }

  return (
    <Accordion.Item
      title="Theme"
      summary="email width, colors, font and language"
    >
      <FormStack>
        <Input
          label="Content width (px)"
          type="number"
          min={320}
          max={800}
          value={String(theme.width)}
          onChange={(e) => {
            const n = Number(e.target.value);
            set("width", Number.isFinite(n) && n > 0 ? n : theme.width);
          }}
        />
        {COLOR_FIELDS.map(({ key, label }) => {
          const value = String(theme[key]);
          return (
            <div key={key} className={styles.colorRow}>
              {/* The kit ships no colour-picker primitive; this native swatch
                  pairs with the lib <Input> holding the hex value. */}
              <input
                type="color"
                data-composed-control
                className={styles.colorSwatch}
                aria-label={`${label} — color picker`}
                value={isHexColor(value) ? value : "#000000"}
                onChange={(e) => set(key, e.target.value)}
              />
              <Input
                label={label}
                value={value}
                spellCheck={false}
                onChange={(e) => set(key, e.target.value)}
              />
            </div>
          );
        })}
        <Input
          label="Font (font-family)"
          value={theme.font_family}
          spellCheck={false}
          onChange={(e) => set("font_family", e.target.value)}
          hint="E.g. Helvetica, Arial, sans-serif"
        />
        <Input
          label="Language"
          value={theme.lang}
          spellCheck={false}
          onChange={(e) => set("lang", e.target.value)}
          hint='2-letter code of the email content language (e.g. "en", "cs").'
        />
      </FormStack>
    </Accordion.Item>
  );
}
